import * as THREE from "three";

/**
 * Where the interior glow light hangs, measured from the BUILT scene.
 *
 * The glow is the warm fill that makes a lit sauna read through its glass
 * at dusk. It used to sit at a fixed world point near the scene origin,
 * which is wherever the building's centre happens to be, not where the
 * hot room is. In a two-room layout the divider runs through that centre,
 * so the light sat a few centimetres from a wall panel and painted a round
 * hot spot on it (1/d² at d ≈ 0.1 m), seen through the relax-room glass.
 *
 * Anchored instead over the middle of the hot room's own furniture (the
 * aggregat and the lavar), a little above the top seat, where a ceiling
 * lamp would light the room from. That point is inside the room and clear
 * of every wall by construction. World space, because the light lives
 * outside the rotated rig.
 */
const HOT_ROOM_PREFIXES = ["detail/heater/", "detail/bench-slat/"];
/** Above the highest seat: about head height of someone on the top lav. */
const ABOVE_TOP_SEAT_M = 0.55;

export function planInteriorGlowAnchor(
	root: THREE.Object3D | null | undefined,
): [number, number, number] | null {
	const rig = root?.getObjectByName?.("sauna-rig") ?? null;
	if (!rig) return null;
	rig.updateWorldMatrix(true, true);
	const footprint = new THREE.Box3();
	let seatTop = Number.NEGATIVE_INFINITY;
	rig.traverse((object) => {
		if (!(object instanceof THREE.Mesh)) return;
		if (!HOT_ROOM_PREFIXES.some((prefix) => object.name.startsWith(prefix))) {
			return;
		}
		// Relax-room furniture is not in the hot room.
		if (object.name.includes("relax")) return;
		const box = new THREE.Box3().setFromObject(object);
		if (box.isEmpty()) return;
		footprint.union(box);
		if (object.name.startsWith("detail/bench-slat/")) {
			seatTop = Math.max(seatTop, box.max.y);
		}
	});
	if (footprint.isEmpty() || !Number.isFinite(seatTop)) return null;
	const centre = footprint.getCenter(new THREE.Vector3());
	return [centre.x, seatTop + ABOVE_TOP_SEAT_M, centre.z];
}
